import { Check, ChevronsUpDown, Globe, History, LogOut, Network, Plus, Redo2, Undo2, UserRound } from "lucide-react";
import { Mark } from "@/components/brand/Mark";
import { NavLink } from "react-router-dom";
import { NodeIcon } from "@/components/NodeIcon";
import { NewScenarioPopover } from "@/components/shell/NewScenarioPopover";
import { RECENT_SHOWN, useRecent } from "@/components/shell/recent";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { useCanEdit } from "@/model/access";
import { cn } from "@/lib/utils";
import { useStore } from "@/model/store";

const item = ({ isActive }: { isActive: boolean }) =>
	cn("flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-sidebar-foreground hover:bg-white/10", isActive && "bg-white/20 font-medium");

const sideBtn = "text-sidebar-foreground hover:bg-white/10 hover:text-sidebar-foreground disabled:text-sidebar-foreground/50";

// White at 90% on the sidebar green keeps small labels above 4.5:1.
const groupLabel = "flex items-center justify-between px-2 pb-1 text-xs font-medium text-sidebar-foreground/90";

/** The desktop sidebar; below `lg` the same content opens as a drawer from the header. */
export function Sidebar() {
	return (
		<aside className="hidden h-screen w-64 shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground lg:flex">
			<SidebarContent />
		</aside>
	);
}

export function SidebarContent() {
	const world = useStore((s) => s.world);
	const analyst = useStore((s) => s.analyst);
	const setAnalyst = useStore((s) => s.setAnalyst);
	const undo = useStore((s) => s.undo);
	const redo = useStore((s) => s.redo);
	const canUndo = useStore((s) => s.canUndo);
	const canRedo = useStore((s) => s.canRedo);
	const active = useStore((s) => s.activeScenario);
	const recentIds = useRecent((s) => s.ids);
	const recent = recentIds
		.map((id) => world.nodes[id])
		.filter(Boolean)
		.slice(0, RECENT_SHOWN);
	const canEdit = useCanEdit();

	return (
		<>
			<div className="px-5 pt-5 pb-4">
				<NavLink to="/" className="block">
					<Wordmark />
				</NavLink>
			</div>
			<nav className="flex-1 space-y-5 overflow-y-auto px-3 pb-4" aria-label="Main">
				<div className="space-y-0.5">
					<NavLink to="/" end className={item}>
						<Globe className="size-4 shrink-0" aria-hidden /> Worldview
					</NavLink>
					<NavLink to="/graph" className={item}>
						<Network className="size-4 shrink-0" aria-hidden /> Graph
					</NavLink>
					<NavLink to="/activity" className={item}>
						<History className="size-4 shrink-0" aria-hidden /> Activity
					</NavLink>
				</div>
				{/* Scenarios change events and metrics, so an empty workspace does not mention them yet. */}
				{(world.scenarios.length > 0 || Object.keys(world.nodes).length > 0) && (
					<div>
						<div className={groupLabel}>
							Scenarios
							{canEdit && (
								<NewScenarioPopover>
									<button
										className="-my-1 flex size-8 items-center justify-center rounded-md hover:bg-white/10 hover:text-sidebar-foreground"
										title="New scenario"
										aria-label="New scenario"
									>
										<Plus className="size-4" />
									</button>
								</NewScenarioPopover>
							)}
						</div>
						{world.scenarios.map((sc) => (
							<NavLink key={sc.id} to={`/scenario/${sc.id}`} className={item}>
								<span className={cn("size-1.5 shrink-0 rounded-full", active === sc.id ? "bg-[var(--scenario)]" : "bg-transparent")} aria-hidden />
								<span className="truncate">{sc.name}</span>
								{active === sc.id && <span className="sr-only">(viewing)</span>}
							</NavLink>
						))}
						{world.scenarios.length === 0 && <p className="px-2 text-xs text-sidebar-foreground/90">None yet.</p>}
					</div>
				)}
				{recent.length > 0 && (
					<div>
						<div className={cn(groupLabel, "pb-1.5")}>Recent</div>
						{recent.map((n) => (
							<NavLink key={n.id} to={`/node/${n.id}`} className={item}>
								<NodeIcon node={n} className="text-xs" />
								<span className="truncate">{n.title}</span>
							</NavLink>
						))}
					</div>
				)}
			</nav>
			<div className="space-y-3 border-t border-white/15 px-4 py-3">
				<label className="flex items-center justify-between gap-2 text-sm">
					Show statistical detail
					<Switch checked={analyst} onCheckedChange={setAnalyst} />
				</label>
				<div className="flex items-center gap-1">
					<Button variant="ghost" size="sm" className={sideBtn} onClick={() => void undo()} disabled={!canUndo} title="Undo (⌘Z)">
						<Undo2 className="size-4" /> Undo
					</Button>
					<Button variant="ghost" size="sm" className={sideBtn} onClick={() => void redo()} disabled={!canRedo} title="Redo (⇧⌘Z)">
						<Redo2 className="size-4" /> Redo
					</Button>
				</div>
				<AccountMenu />
			</div>
		</>
	);
}

/** Who is signed in and which workspace this is: switch workspace, open the account, sign out. */
function AccountMenu() {
	const user = useStore((s) => s.user);
	const workspaces = useStore((s) => s.workspaces);
	const current = useStore((s) => s.ws);
	const signOut = useStore((s) => s.signOut);
	const ws = workspaces.find((w) => w.slug === current);
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-white/10" aria-label="Account and workspace">
					<span className="min-w-0 flex-1">
						<span className="block truncate font-medium">{ws?.name ?? "Workspace"}</span>
						<span className="block truncate text-xs text-sidebar-foreground/80">
							{user?.email}
							{ws?.role === "viewer" && " · view only"}
						</span>
					</span>
					<ChevronsUpDown className="size-4 shrink-0 opacity-70" aria-hidden />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent side="top" align="start" className="w-60">
				{workspaces.length > 1 && (
					<>
						<DropdownMenuLabel className="text-xs text-muted-foreground">Workspaces</DropdownMenuLabel>
						{workspaces.map((w) => (
							<DropdownMenuItem key={w.slug} onSelect={() => w.slug !== current && window.location.assign(`/w/${encodeURIComponent(w.slug)}/`)}>
								<Check className={cn("size-4", w.slug !== current && "invisible")} aria-hidden />
								<span className="min-w-0 flex-1 truncate">{w.name}</span>
								{w.role === "viewer" && <span className="text-xs text-muted-foreground">view only</span>}
							</DropdownMenuItem>
						))}
						<DropdownMenuSeparator />
					</>
				)}
				<DropdownMenuItem asChild>
					<NavLink to="/account">
						<UserRound className="size-4" aria-hidden /> Account and connected apps
					</NavLink>
				</DropdownMenuItem>
				<DropdownMenuItem onSelect={() => void signOut().then(() => window.location.assign("/signin"))}>
					<LogOut className="size-4" aria-hidden /> Sign out
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

/** The lockup: the brand mark beside the name (brand/brand-guide.md). */
export function Wordmark() {
	return (
		<div className="flex items-center gap-2.5">
			<Mark size={24} />
			<span className="text-lg font-semibold tracking-tight">ModelCurve</span>
		</div>
	);
}
